import { useState, useRef, useEffect } from "react";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { CircularProgress, Box } from "@mui/material";
import { CustomLoadingButton } from "../ui/CustomButton";

const ExportToExcelButton = ({
  data,
  exportConfig,
  fileName = "exported_data.xlsx",
  currentTheme,
  borderRadiusType = "all",

  // NEW: if provided, component will not render its own button
  children,
  // optional: show progress ui even in headless mode
  showProgress = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const workerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
    };
  }, []);

  const downloadFile = (buffer, name) => {
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();

    URL.revokeObjectURL(url);
  };

  const cleanupWorker = () => {
    setIsExporting(false);
    setProgress(0);
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  };

  const exportToExcel = () => {
    if (isExporting) return;

    if (typeof Worker === "undefined") {
      alert("Your browser does not support Web Workers.");
      return;
    }

    setIsExporting(true);
    setProgress(0);

    const worker = new Worker(new URL("./excelExportWorker.js", import.meta.url), {
      type: "module",
    });

    workerRef.current = worker;

    worker.postMessage({ data, exportConfig, fileName });

    worker.onmessage = (e) => {
      const { type, progress: p, buffer, fileName: fn } = e.data;

      if (type === "progress") {
        setProgress(p);
      } else if (type === "done") {
        downloadFile(buffer, fn);
        cleanupWorker();
      }
    };

    worker.onerror = (err) => {
      console.error("Excel export worker error:", err);
      cleanupWorker();
    };
  };

  // HEADLESS MODE: you control the UI
  if (typeof children === "function") {
    return (
      <>
        {children({
          exportToExcel,
          isExporting,
          progress,
        })}

        {showProgress && isExporting && (
          <CircularProgress
            variant="determinate"
            value={progress}
            size={24}
            thickness={5}
          />
        )}
      </>
    );
  }

  // DEFAULT MODE: renders its own button (existing behavior)
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <CustomLoadingButton
        currentTheme={currentTheme}
        iconPosition="end"
        endIcon={!isExporting && <ExitToAppIcon />}
        onClick={exportToExcel}
        disabled={isExporting}
        borderRadiusType="both"
        variant="tint"
        color="#43a047"
      >
        {isExporting ? "Exporting..." : "Export to Excel"}
      </CustomLoadingButton>

      {isExporting && (
        <CircularProgress
          variant="determinate"
          value={progress}
          size={24}
          thickness={5}
        />
      )}
    </Box>
  );
};

export default ExportToExcelButton;
